import {
  BlinkIdMultiSideRecognizerResult,
  BlinkIdSingleSideRecognizerResult,
  DetectionStatus,
  DisplayableQuad,
  IdBarcodeRecognizerResult,
  RecognizerResultState,
} from '@microblink/blinkid-in-browser-sdk';

import FrameTrigger, { FrameTriggerConfig } from 'lib/clients/FrameTrigger';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';

export enum GovernmentIdMicroblinkTriggerType {
  DetectedGlare,
  Capture,
  HoldStill,
  MoveCloser,
  MoveAway,
  NotCentered,
  None,
  Processing,
}

export interface GovernmentIdMicroblinkTriggerParams {
  currentTrigger: GovernmentIdMicroblinkTriggerType;
  microblinkResult:
    | BlinkIdSingleSideRecognizerResult
    | IdBarcodeRecognizerResult
    | BlinkIdMultiSideRecognizerResult
    | null;
  glareRatio: number;
  /**
   * NOTE: Currently this can be an "empty" quad, e.g. one where detectionStatus is Failed.
   * Consumers can't rely on null checks of this attribute.
   */
  detectedQuad: DisplayableQuad | null;
  videoWidth: number;
  videoHeight: number;
  hasGlare: boolean | null;
  imageDataUri: string | null;
  processingTime?: number | null;
  microblinkRecognizerName?: string | null;
}

/**
 * NOTE: DO NOT CHANGE ANY TIMINGS BELOW.
 *
 * We've committed to not changing timings for Chime.
 * If changing, please ask in #c-chime-internal.
 */
export const DETECT_INTERVAL = 100;
const MIN_WIDTHS = {
  [GovernmentIdSide.Front]: 0.6,
  // Microblink only detects the barcode quad when scanning the back of the ID
  [GovernmentIdSide.Back]: 0.3,
  [GovernmentIdSide.FrontOrBack]: 0.6,
  [GovernmentIdSide.BarcodePdf417]: 0.4,
  [GovernmentIdSide.PassportSignature]: 0.4,
};

const MAX_WIDTHS = {
  [GovernmentIdSide.Front]: 0.9,
  // Microblink only detects the barcode quad when scanning the back of the ID
  [GovernmentIdSide.Back]: 0.75,
  [GovernmentIdSide.FrontOrBack]: 0.9,
  [GovernmentIdSide.BarcodePdf417]: 0.9,
  [GovernmentIdSide.PassportSignature]: 0.9,
};

type MicroblinkFrameTriggerConfig = Array<
  FrameTriggerConfig<GovernmentIdMicroblinkTriggerType, GovernmentIdMicroblinkTriggerParams>
>;

export const GOVERNMENT_ID_FRONT_TRIGGER_CONFIGS: MicroblinkFrameTriggerConfig = [
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedGlare,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    shouldShow: detectsGlare,
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-glare'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveCloser,
    shouldShow: (params) => tooFar(GovernmentIdSide.Front, params),
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-closer'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveAway,
    shouldShow: (params) => tooClose(GovernmentIdSide.Front, params),
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-away'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.NotCentered,
    shouldShow: (params) => notCentered(params) && !tooFar(GovernmentIdSide.Front, params),
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.outside-of-frame'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export const GOVERNMENT_PDF417_BARCODE_TRIGGER_CONFIGS: MicroblinkFrameTriggerConfig = [
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedGlare,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    shouldShow: detectsGlare,
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-glare'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveCloser,
    shouldShow: (params) => tooFar(GovernmentIdSide.BarcodePdf417, params),
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-closer'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveAway,
    shouldShow: (params) => tooClose(GovernmentIdSide.BarcodePdf417, params),
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-away'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export const GOVERNMENT_ID_BACK_TRIGGER_CONFIGS: MicroblinkFrameTriggerConfig = [
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedGlare,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    shouldShow: detectsGlare,
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-glare'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveCloser,
    shouldShow: (params) => tooFar(GovernmentIdSide.Back, params),
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-closer'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveAway,
    shouldShow: (params) => tooClose(GovernmentIdSide.Back, params),
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-away'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.NotCentered,
    shouldShow: (params) => notCentered(params) && !tooFar(GovernmentIdSide.Front, params),
    showAfterFrames: 3,
    hideAfterFrames: 10,
    getPrompt: ({ t }) => t('government-id-scanner.hint.outside-of-frame'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export const TRIGGER_CONFIGS_BY_SIDE: Record<GovernmentIdSide, MicroblinkFrameTriggerConfig> = {
  [GovernmentIdSide.Front]: GOVERNMENT_ID_FRONT_TRIGGER_CONFIGS,
  [GovernmentIdSide.Back]: GOVERNMENT_ID_BACK_TRIGGER_CONFIGS,
  [GovernmentIdSide.BarcodePdf417]: GOVERNMENT_PDF417_BARCODE_TRIGGER_CONFIGS,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [GovernmentIdSide.FrontOrBack]: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [GovernmentIdSide.PassportSignature]: undefined,
};

export const PROCESSING_TRIGGER = new FrameTrigger({
  // This trigger is just used to trigger the screenshot
  type: GovernmentIdMicroblinkTriggerType.Processing,
  shouldShow: () => true,
  showAfterFrames: 0,
  hideAfterFrames: 0,
  getPrompt: ({ t }) =>
    t('government-id-scanner.hint.processing', {
      defaultValue: t('government-id-scanner.hint.hold-still'),
    }),
});

export function detectsGlare(params: GovernmentIdMicroblinkTriggerParams) {
  // NB: this is looser than should capture's threshold
  return (params.glareRatio > 0.0002 || params.hasGlare) && microblinkDetectedId(params);
}

export function microblinkDetectedId(params: GovernmentIdMicroblinkTriggerParams) {
  return params.microblinkResult?.state === RecognizerResultState.Valid;
}

export function shouldCapture(params: GovernmentIdMicroblinkTriggerParams) {
  return microblinkDetectedId(params);
}

export function tooFar(side: GovernmentIdSide, params: GovernmentIdMicroblinkTriggerParams) {
  const quad = params.detectedQuad;
  if (!quad) {
    return false;
  }

  const quadWidth = Math.max(
    quad.topRight.x - quad.topLeft.x,
    quad.bottomRight.x - quad.bottomLeft.x,
  );
  if (quadWidth > 0 && quadWidth / params.videoWidth < MIN_WIDTHS[side]) {
    return true;
  }

  return params.detectedQuad?.detectionStatus === DetectionStatus.CameraTooFar;
}

export function tooClose(side: GovernmentIdSide, params: GovernmentIdMicroblinkTriggerParams) {
  const quad = params.detectedQuad;
  if (!quad) {
    return false;
  }
  const quadWidth = Math.max(
    quad.topRight.x - quad.topLeft.x,
    quad.bottomRight.x - quad.bottomLeft.x,
  );
  if (quadWidth > 0 && quadWidth / params.videoWidth > MAX_WIDTHS[side]) {
    return true;
  }
  return params.detectedQuad?.detectionStatus === DetectionStatus.CameraTooClose;
}

export function notCentered(params: GovernmentIdMicroblinkTriggerParams) {
  return (
    params.detectedQuad?.detectionStatus != null &&
    [
      DetectionStatus.DocumentTooCloseToCameraEdge,
      DetectionStatus.DocumentPartiallyVisible,
    ].includes(params.detectedQuad?.detectionStatus)
  );
}

export function validId(params: GovernmentIdMicroblinkTriggerParams) {
  return params.detectedQuad?.detectionStatus === DetectionStatus.Success;
}

export function hasValidMrz(
  governmentIdClass: GovernmentIdClass,
  params: GovernmentIdMicroblinkTriggerParams,
) {
  const isNotPassport = governmentIdClass !== 'pp';
  const mrzIsVerified =
    params.microblinkResult && 'mrz' in params.microblinkResult
      ? params.microblinkResult.mrz.verified === true
      : false;
  return isNotPassport || mrzIsVerified;
}
